"use client";

import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import styles from "./map-view.module.css";
import { useLocale, useTranslations } from "next-intl";
import { env } from "@/lib/env";

// Importing modern clean outline icons matching V2 branding
import {
  IoMapOutline,
  IoEllipsisHorizontal,
  IoWalkOutline,
  IoSchoolOutline,
  IoStorefrontOutline,
  IoTrainOutline,
} from "react-icons/io5";

const mapUrl = env.NEXT_MAP_URL;

// Mock POI data representing what your backend will pass down
const MOCK_POIS = [
  { name: "Parcul Herăstrău", category: "park", distance_minutes: 5 },
  { name: "Metrou Aurel Vlaicu", category: "subway", distance_minutes: 8 },
  { name: "Promenada Mall", category: "mall", distance_minutes: 10 },
  { name: "Școala Britanică", category: "school", distance_minutes: 12 },
];

interface MapViewProps {
  location: {
    lat: number;
    long: number;
  };
  locationOnRequest: boolean;
}

export default function MapView({ location, locationOnRequest }: MapViewProps) {
  const t = useTranslations("MapView");
  const locale = useLocale();
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Helper to resolve beautiful minimal categorical icons
  const getPoiIcon = (category: string) => {
    switch (category) {
      case "park":
        return <IoMapOutline className={styles.poiIcon} />;
      case "school":
        return <IoSchoolOutline className={styles.poiIcon} />;
      case "mall":
        return <IoStorefrontOutline className={styles.poiIcon} />;
      case "subway":
        return <IoTrainOutline className={styles.poiIcon} />;
      default:
        return <IoEllipsisHorizontal className={styles.poiIcon} />;
    }
  };

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    (async () => {
      if (!mapContainer.current || mapRef.current) return;
      const response = await fetch("/map/my-style.json");
      const style = await response.json();
      const userLocale = locale;

      // Localize text layers inside your raw stylesheet
      const nameExpression = [
        "coalesce",
        ["get", `name:${userLocale}`],
        ["get", "name:en"],
        ["get", "name"],
      ];

      style.layers = style.layers.map((layer: any) => {
        const layout = layer.layout;
        if (!layout) return layer;
        const field = layout["text-field"];
        if (typeof field === "string" && field.includes("{name")) {
          layout["text-field"] = nameExpression;
        }
        if (Array.isArray(field) && field[0] === "get" && field[1] === "name") {
          layout["text-field"] = nameExpression;
        }
        return layer;
      });

      // Initialize PMTiles protocol safely
      const protocol = new Protocol();
      maplibregl.addProtocol("pmtiles", (params, cb) =>
        protocol.tile(params, cb),
      );

      const map = new maplibregl.Map({
        container: mapContainer.current,
        style,
        center: [location.long, location.lat],
        zoom: 14,
        minZoom: 3,
        maxZoom: 18,
        pitch: 0,
        bearing: 0,
        attributionControl: false,
        cooperativeGestures: true,
      });

      mapRef.current = map;
      map.once("load", () => setMapReady(true));

      map.once("load", () => {
        const markerEl = document.createElement("div");
        markerEl.className = styles.mapMarkerPin;

        if (locationOnRequest) {
          // Approximate Location Display
          markerEl.innerHTML = `
            <div class="${styles.approximateCircleOuter}">
              <div class="${styles.approximateCircleInner}"></div>
            </div>
          `;
          new maplibregl.Marker({ element: markerEl, anchor: "center" })
            .setLngLat([location.long, location.lat])
            .addTo(map);
        } else {
          // Exact Location Display (Sleek dark teardrop)
          markerEl.innerHTML = `
            <div class="${styles.exactMarkerTeardrop}">
              <svg fill="currentColor" viewBox="0 0 24 24" class="${styles.exactMarkerSvg}">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
            </div>
          `;
          new maplibregl.Marker({ element: markerEl, anchor: "bottom" })
            .setLngLat([location.long, location.lat])
            .addTo(map);
        }
      });

      return () => {
        map.remove();
        mapRef.current = null;
        setMapReady(false);
      };
    })();
  }, [location, locale, locationOnRequest]);

  return (
    <div className={styles.widgetCard}>
      {/* Map Canvas Wrapper */}
      <div className={styles.canvasWrapper}>
        <div ref={mapContainer} className={styles.mapCanvas} />
      </div>

      {/* Points of Interest List View */}
      <div className={styles.poiSection}>
        <h3 className={styles.poiSectionTitle}>Puncte de interes</h3>

        <div className={styles.poiGrid}>
          {MOCK_POIS.map((poi, index) => (
            <div key={index} className={styles.poiItem}>
              <div className={styles.poiLeft}>
                {getPoiIcon(poi.category)}
                <span className={styles.poiName}>{poi.name}</span>
              </div>
              <div className={styles.poiRight}>
                <IoWalkOutline className={styles.walkIcon} />
                <span className={styles.poiTime}>
                  {poi.distance_minutes} min
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
